/* Infinite scroll for collection grids (sections/main-collection-product-grid.liquid → "Infinite scroll").
   When the shopper nears the end of the grid, the next page's cards are fetched with the section rendering API
   and appended. The normal page links stay in the HTML for search engines and as a fallback if loading fails. */
if (!customElements.get('adelaide-infinite-scroll')) {
  customElements.define('adelaide-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      // data-grid names the list that holds the cards (search results nest it inside #product-grid).
      this.gridSelector = this.dataset.grid || '#product-grid';
      this.grid = document.querySelector(this.gridSelector);
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      this.status = this.querySelector('[data-infinite-status]');
      if (!this.grid || !this.sentinel || !this.dataset.nextUrl || !('IntersectionObserver' in window)) return;
      this.classList.add('is-active');
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 900px 0px' });
      this.observer.observe(this.sentinel);
      // Backup trigger for browsers whose observer misses fast scrolls.
      this.onScroll = () => {
        if (this.scrollQueued) return;
        this.scrollQueued = true;
        setTimeout(() => {
          this.scrollQueued = false;
          if (this.sentinel.getBoundingClientRect().top < window.innerHeight + 900) this.load();
        }, 150);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    async load() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');
      try {
        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = [...doc.querySelectorAll(`${this.gridSelector} > .grid__item`)];
        const fragment = document.createDocumentFragment();
        // Search results with equal relevance can shift between page requests; skip a cake that is already shown.
        const productKey = (item) => item.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0];
        const shown = new Set([...this.grid.querySelectorAll(':scope > .grid__item')].map(productKey).filter(Boolean));
        items.forEach((item) => {
          const key = productKey(item);
          if (key && shown.has(key)) return;
          if (key) shown.add(key);
          // Reveal-on-scroll classes would leave appended cards invisible; they arrive already in view.
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          fragment.append(item);
        });
        this.grid.append(fragment);
        this.dataset.nextUrl = doc.querySelector('adelaide-infinite-scroll')?.dataset.nextUrl || '';
        if (this.status) {
          const total = this.grid.querySelectorAll(':scope > .grid__item').length;
          this.status.textContent = (this.dataset.loadedLabel || 'Showing [count] cakes').replace('[count]', total);
        }
        if (!this.dataset.nextUrl) {
          this.observer.disconnect();
          window.removeEventListener('scroll', this.onScroll);
          this.classList.add('is-done');
        }
      } catch (error) {
        // Fall back to the page links.
        this.observer?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        this.classList.remove('is-active');
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }
      // Short pages or fast scrolling can leave the sentinel in range without a new intersection event.
      if (this.dataset.nextUrl && this.classList.contains('is-active')) {
        const rect = this.sentinel.getBoundingClientRect();
        if (rect.top < window.innerHeight + 900) this.load();
      }
    }
  });
}
